import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { useFormikContext } from 'formik';
import {
  Section,
  ConnectedSelect,
  MaskedInput as Input,
  NumberInput,
  withForm,
  Modal,
  ActionModal,
  Icons,
  Checkbox,
  Select,
} from '@casanova/casanova-common';
import { MODES } from '@payments/commons/constants';
import { SearchableSelect } from 'components';
import {
  alphaMaskWithSpaces,
  lettersAndNumbersAndAccentsWithSpace,
} from '@casanova/casanova-common/lib/utils/masks';
import i18n from '@i18n';
import {
  IT_ADMIN,
  BRANCH_A_MANAGER,
  BRANCH_B_MANAGER,
  BONUSES_MANAGER,
} from 'utils/roles/roles';
import { useDispatch } from 'react-redux';
import {
  savePayment,
  closeResponseDialogValidCode,
} from 'store/payments/actions';
import { alphaMaskWithSpacesAndAccentAndSpecialChars } from 'utils/masks';
import { MultiPayments } from '../sectionsMethodsPayment';
import ResponseDialog from '../../../components/ResponseDialog';
import {
  validateElement,
  validateRole,
  validateRolePermissions,
} from '../../../utils/roles';
import formatter from './formatter';
import { CodeModal } from '../../../components/CodeModal';
import { config } from './config';
import { getRoleName } from '../../../services/auth';
import {
  PAYMENTS_PER_BRANCH_NO_FILTER,
  ADD_BONUS,
  BONUS_PER_BRANCH_NO_FILTER,
  CHARGES_PER_BRANCH_NO_FILTER,
} from '../../../utils/roles/permissions';

function PaymentDetail({
  openConfirmationMessage,
  setHideModal,
  responseDialog,
  loader,
  mode,
  setMode,
  searchEmployees,
  fetchCatalog,
  onUpdateForm,
  movementTypes,
  contractDetails,
  customerJuridical,
  getDetail,
  handleGetValues,
  branches,
}) {
  const [checkBonificacion, setCheckBonificacion] = useState(false);
  console.log('branches', branches);

  const { setFieldValue, values, touched } = useFormikContext();
  const isJuridical = Boolean(customerJuridical);
  const naturalPersonUuid = useMemo(() => contractDetails.naturalPerson, [
    contractDetails.naturalPerson,
  ]);

  const billDetails = {
    customerFullName: contractDetails.naturalPersonName,
    folio: contractDetails.number,
    concept: values.concept,
  };

  const handleSearchEmployee = (query) => {
    if (query && query.length > 2) {
      searchEmployees(query);
    }
  };

  useEffect(() => {
    handleGetValues(values);
  }, [values]);

  useEffect(() => {
    if (!_isEmpty(contractDetails) && movementTypes.length > 0) {
      const movement = movementTypes.find((type) => type.identifier === mode);
      if (movement) {
        onUpdateForm({
          ...contractDetails,
          movementType: movement.uuid,
        });
      }
    }
  }, [onUpdateForm, contractDetails, movementTypes, mode]);

  useEffect(() => {
    setHideModal(openConfirmationMessage || responseDialog.open || loader);
  }, [openConfirmationMessage, responseDialog.open, loader]);

  const handleChangePaymentType = () => {
    if (checkBonificacion) {
      setCheckBonificacion(false);
      setFieldValue('wantInvoice', false);
      setMode(MODES.PAYMENT);
      return;
    }
    setCheckBonificacion(true);
    setMode(MODES.BONUS);
  };

  useEffect(() => {
    getDetail({ id: contractDetails.naturalPerson, withLoader: true });
  }, [naturalPersonUuid]);

  return (
    <>
      <Section
        title={i18n(`PAYMENTS_AND_CHARGES__PAYMENT_DETAIL__TITLE_${mode}`)}
      >
        <div className="row">
          {(mode === 'PAYMENT' || mode === 'BONUS') &&
            validateRolePermissions(ADD_BONUS) && (
              <div className="col-md-12 text-right">
                <Checkbox
                  name="checkBonificacion"
                  label="BONIFICACIÓN"
                  disabled={!validateElement(ADD_BONUS)}
                  onChange={handleChangePaymentType}
                />
              </div>
            )}
          <div className="col-md-4">
            <NumberInput
              label={`${i18n(
                'PAYMENTS_AND_CHARGES__CONTRACT_DETAIL__AMOUNT'
              )}*`}
              placeholder={`${i18n(
                'PAYMENTS_AND_CHARGES__CONTRACT_DETAIL__AMOUNT'
              )}*`}
              decimals={2}
              max={999_999}
              name="amount"
            />
          </div>
          <div className="col-md-4">
            <Select
              label={`${i18n('COMMONS__BRANCH')}*`}
              placeholder={`${i18n('COMMONS__BRANCH')}*`}
              name="branch"
              // options="commons.branches"
              options={
                branches?.map((item) => ({
                  uuid: item.uuid,
                  value: item.uuid,
                  label: item.name,
                  id: item.id,
                })) || []
              }
              // options={[]}
              disabled={
                (mode === 'PAYMENT' &&
                  !validateElement(PAYMENTS_PER_BRANCH_NO_FILTER)) ||
                (mode === 'CHARGE' &&
                  !validateElement(CHARGES_PER_BRANCH_NO_FILTER)) ||
                (mode === 'BONUS' &&
                  !validateElement(BONUS_PER_BRANCH_NO_FILTER))
              }
            />
          </div>
          {false && (
            <div className="col-md-4">
              <ConnectedSelect
                label="Forma de pago*"
                name="paymentForm"
                options="commons.catalogsPaymentForms"
              />
            </div>
          )}
          <div className="col-md-4">
            <Input
              mask={alphaMaskWithSpacesAndAccentAndSpecialChars(140)}
              maskPlaceholder=""
              label={`${i18n(
                'PAYMENTS_AND_CHARGES__CONTRACT_DETAIL__LIST__CONCEPT'
              )}*`}
              placeholder={`${i18n(
                'PAYMENTS_AND_CHARGES__CONTRACT_DETAIL__LIST__CONCEPT'
              )}*`}
              name="concept"
            />
          </div>
          <div className="col-md-4">
            <SearchableSelect
              name="requester"
              label="SOLICITANTE*"
              placeholder="SOLICITANTE*"
              options="customers.employees"
              mask={alphaMaskWithSpaces(200)}
              onSearching={handleSearchEmployee}
              icon="svg-icon-search"
            />
          </div>
          <div className="col-md-4">
            <Input
              label={`${i18n('PAYMENTS_AND_CHARGES__PAYMENT_DETAIL__REASON')}*`}
              placeholder={`${i18n(
                'PAYMENTS_AND_CHARGES__PAYMENT_DETAIL__REASON'
              )}*`}
              mask={alphaMaskWithSpacesAndAccentAndSpecialChars(70)}
              maskPlaceholder=""
              name="reason"
            />
          </div>
          <div className="col-md-4">
            <Input
              label={`${i18n('COMMONS__REMARKS')}`}
              placeholder={`${i18n('COMMONS__REMARKS')}`}
              mask={alphaMaskWithSpacesAndAccentAndSpecialChars(70)}
              maskPlaceholder=""
              name="observations"
            />
          </div>
          <div className="col-md-4">
            <Input
              mask={lettersAndNumbersAndAccentsWithSpace(40)}
              maskPlaceholder=""
              label="Error de"
              placeholder="Error de"
              name="error"
            />
          </div>
        </div>
      </Section>

      {mode === 'PAYMENT' && (
        <MultiPayments
          touched={touched}
          onUpdateForm={onUpdateForm}
          uuidCustomer={naturalPersonUuid}
          fetchCatalog={fetchCatalog}
          isJuridical={isJuridical}
          billDetails={billDetails}
        />
      )}
    </>
  );
}

export default function PaymentDetailContainer({
  loader,
  responseDialogValidCode,
  show,
  onClose,
  ...props
}) {
  // const dispatch = useDispatch();
  const { mode } = props;
  const [hideModal, setHideModal] = useState(false);
  // const [showCodeModal, setShowCodeModal] = useState(false);
  const [showConfirmModal, setConfirmModal] = useState(false);
  const [formValues, setFormValues] = useState({});

  const handleGetValues = (paymentsValues) => {
    setFormValues(formatter(paymentsValues));
  };

  const handleCancel = ({ dirty, submitted }) => {
    setConfirmModal(false);

    if (dirty && !submitted) {
      setConfirmModal(true);
      setHideModal(true);
      return;
    }

    setHideModal(false);
    onClose();
  };

  // const handleClickSubmit = () => {
  //   if (mode === MODES.BONUS && !validateRolePermissions(ADD_BONUS)) {
  //     setHideModal(true);
  //     setShowCodeModal(true);
  //   }
  // };

  // const handleOnCloseCodeModal = () => {
  //   setShowCodeModal(false);
  //   setHideModal(false);
  // };

  // const handleConfirmCode = (codeForm = {}) => {
  //   setShowCodeModal(false);
  //   dispatch(savePayment({ ...formValues, validCodeRequest: codeForm }));
  // };

  // const handleOnCloseValidCode = () => {
  //   dispatch(closeResponseDialogValidCode());
  //   setShowCodeModal(true);
  // };

  // const newConfig = {
  //   ...config(mode),
  //   disableSubmit: mode === MODES.BONUS && !validateRolePermissions(ADD_BONUS),
  //   handleClickSubmit,
  // };

  const PaymentWithForm = withForm({
    // config: newConfig,
    config: { ...config(mode) },
  })(PaymentDetail);

  return (
    <Modal
      open={show}
      onClose={onClose}
      bigModal
      topContainerClassName={hideModal || loader ? 'd-none' : ''}
    >
      <ActionModal
        open={showConfirmModal}
        icon={<Icons.StatusWarning />}
        onClose={() => {
          setConfirmModal(false);
          setHideModal(false);
        }}
        onAction={() => {
          setConfirmModal(false);
          setHideModal(false);
          onClose();
        }}
        title={i18n('PREVENT_CHANGE__TITLE')}
        message={i18n('PREVENT_CHANGE__MESSAGE')}
        actionLabel={i18n('PREVENT_CHANGE__ACTION')}
        closeLabel={i18n('PREVENT_CHANGE__CLOSE')}
        closeButton
      />
      {/* <ResponseDialog
        open={responseDialogValidCode.open}
        errorTitle={i18n('ERROR__COMMONS__ERROR_IN_REQUEST__TITLE')}
        errorLabel={i18n('COMMONS__CLOSE__TEXT')}
        errorMessage={responseDialogValidCode.errorMessage}
        errorCode={responseDialogValidCode.errorCode}
        onError={handleOnCloseValidCode}
        onClose={handleOnCloseValidCode}
      /> */}
      {/* <CodeModal
        open={showCodeModal && !responseDialogValidCode.open && !loader}
        message="gerente de sucursal"
        title="Realizar Bonificación"
        onAction={handleConfirmCode}
        onClose={handleOnCloseCodeModal}
      /> */}
      {PaymentWithForm({
        ...props,
        closeResponseDialog: () => {
          props.closeResponseDialog();
          onClose();
        },
        onCancel: handleCancel,
        onCancelPath: './',
        onSuccess: () => onClose(),
        setHideModal,
        handleGetValues,
      })}
    </Modal>
  );
}

PaymentDetailContainer.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setNewInvoicePayment: PropTypes.func,
};
